import {
  Area,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { Indicator, TrajectoryPoint } from "../../lib/climate";

/**
 * One indicator from 1976 to 2100.
 *
 * The house convention, same as the population and price projections: solid is
 * recorded, dashed inside a translucent band is imagined. Here the band is the
 * spread across every emissions pathway, not a confidence interval — the width
 * of it is a disagreement about what humans will do, not about what the physics
 * does.
 */
export function TrajectoryChart({
  points,
  indicator,
}: {
  points: TrajectoryPoint[];
  indicator: Indicator;
}) {
  const data = points.map((p) => ({
    year: p.year,
    label: p.label,
    value: p.value,
    observed: p.observed,
    // Recharts stacks an area from a [low, high] tuple, which is how the
    // spread is drawn as a band rather than two lines.
    band: p.low != null && p.high != null ? [p.low, p.high] : null,
  }));

  return (
    <div className="h-40 w-full">
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 6, right: 6, bottom: 0, left: -6 }}>
          <XAxis
            dataKey="year"
            type="number"
            domain={["dataMin - 5", "dataMax + 5"]}
            ticks={data.map((d) => d.year)}
            tick={{ fontSize: 9, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            width={34}
          />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e3e7ed" }}
            labelFormatter={(_v, payload) => payload?.[0]?.payload?.label ?? ""}
            formatter={(value, name) => {
              if (name === "band" && Array.isArray(value)) {
                return [
                  `${indicator.format(Number(value[0]))} – ${indicator.format(Number(value[1]))}`,
                  "across scenarios",
                ];
              }
              if (value == null) return ["", ""];
              return [
                indicator.format(Number(value)),
                name === "observed" ? "reference period" : "this scenario",
              ];
            }}
          />
          <Area
            dataKey="band"
            stroke="none"
            fill="#2f6df6"
            fillOpacity={0.12}
            isAnimationActive={false}
            connectNulls
          />
          <Line
            dataKey="value"
            stroke="#2f6df6"
            strokeWidth={1.8}
            strokeDasharray="4 3"
            dot={{ r: 2.5, fill: "#2f6df6", strokeWidth: 0 }}
            isAnimationActive={false}
            connectNulls
          />
          <Line
            dataKey="observed"
            stroke="#14181f"
            strokeWidth={2}
            dot={{ r: 3, fill: "#14181f", strokeWidth: 0 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Reading key, rendered by the panel rather than by recharts. */
export function TrajectoryKey() {
  return (
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[9px] text-ink-soft">
      <span className="flex items-center gap-1">
        <span className="inline-block h-0.5 w-3 bg-[#14181f]" />
        1976–2005 reference
      </span>
      <span className="flex items-center gap-1">
        <span className="inline-block h-0.5 w-3 border-t-2 border-dashed border-[#2f6df6]" />
        this scenario
      </span>
      <span className="flex items-center gap-1">
        <span className="inline-block h-2 w-3 rounded-sm bg-[#2f6df6]/20" />
        all scenarios
      </span>
    </div>
  );
}
